import { PromptAuditEntry } from "./types";

interface PromptAuditLogProps {
  entries: PromptAuditEntry[] | undefined;
  isLoading: boolean;
  isError?: boolean;
  companySelected: boolean;
  activeDesignSessionId?: string;
  onRestoreVersion?: (entry: PromptAuditEntry) => void;
}

const ACTION_LABELS: Record<string, string> = {
  generated: "Generated",
  edited: "Edited",
  tested: "Tested",
  saved: "Saved",
};

function formatTimestamp(value?: string): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function metadataString(
  entry: PromptAuditEntry,
  key: string
): string | undefined {
  const value = entry.audit_metadata?.[key];
  if (typeof value === "string" && value.trim()) return value;
  return undefined;
}

const PromptAuditLog = ({
  entries,
  isLoading,
  isError,
  companySelected,
  activeDesignSessionId,
  onRestoreVersion,
}: PromptAuditLogProps) => {
  const total = entries?.length ?? 0;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-gray-800">3. Logging</h2>
      <p className="mt-1 text-sm text-gray-500">
        Each generate, test, and save is logged as a new version for this
        company.
      </p>

      {!companySelected ? (
        <p className="mt-4 text-sm text-gray-400">
          Select a company to view its activity.
        </p>
      ) : isLoading ? (
        <p className="mt-4 text-sm text-gray-400">Loading activity…</p>
      ) : isError ? (
        <p className="mt-4 text-sm text-red-600">
          Could not load activity. Refresh the page and try again.
        </p>
      ) : !entries || entries.length === 0 ? (
        <p className="mt-4 text-sm text-gray-400">No activity recorded yet.</p>
      ) : (
        <table className="mt-4 w-full text-left text-sm">
          <thead>
            <tr className="text-gray-500">
              <th className="pb-2">Version</th>
              <th className="pb-2">When</th>
              <th className="pb-2">Action</th>
              <th className="pb-2">Source</th>
              <th className="pb-2">Actor</th>
              <th className="pb-2">Restore</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => {
              const rowId = entry.external_id ?? `${entry.created_at}-${index}`;
              const versionNumber = total - index;
              const canRestore = Boolean(
                metadataString(entry, "prompt_payload")
              );
              const isActiveGeneration =
                entry.action === "generated" &&
                metadataString(entry, "design_session_id") ===
                  activeDesignSessionId;

              return (
                <tr key={rowId} className="border-t border-gray-100 align-top">
                  <td className="py-2 text-gray-500">
                    <span>v{versionNumber}</span>
                    {isActiveGeneration && (
                      <span
                        className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700"
                        data-testid="active-generation-badge"
                      >
                        Active generation
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-gray-700">
                    {formatTimestamp(entry.created_at)}
                  </td>
                  <td className="py-2 text-gray-700">
                    {ACTION_LABELS[entry.action ?? ""] ?? entry.action ?? "—"}
                  </td>
                  <td className="py-2 text-gray-500">
                    {entry.source_filename || entry.input_source || "—"}
                  </td>
                  <td className="py-2 text-gray-500">
                    {entry.created_by ?? "—"}
                  </td>
                  <td className="py-2">
                    {canRestore && onRestoreVersion ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-green-700 hover:text-green-800"
                        onClick={() => onRestoreVersion(entry)}
                      >
                        Restore
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default PromptAuditLog;
